import { CheckboxIndicator, Checkbox as CheckboxPrimitive } from '@radix-ui/react-checkbox'
import {
  ScrollArea,
  ScrollAreaCorner,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from '@radix-ui/react-scroll-area'

import { DIVIDER_HEIGHT } from '../constants/global'
import { styled } from './stitches.config'

export const Div = styled('div')
export const P = styled('p')
export const B = styled('b')
export const Span = styled('span', {
  variants: {
    flexFillSpace: {
      true: {
        flex: '1 0 0',
      },
    },
    noWrap: {
      true: {
        whiteSpace: 'nowrap',
      },
    },
  },
})

export const Panel = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '1rem',
  border: '0.025rem solid $neutral8',
  borderRadius: '0.5rem',
  padding: '2rem',
})

export const Container = styled('div', {
  maxWidth: '42rem',
  margin: 'auto',
  padding: '7rem 1rem 0 1rem',
  display: 'flex',
  flexDirection: 'column',
  variants: {
    inlineHeaderWithButtons: {
      true: {
        alignItems: 'center',
        gap: '0.625rem',
        alignSelf: 'stretch',
        flex: '1 0 0',
        flexDirection: 'row',
        '> h1, > h2, > h3, > h4, > h5, > h6': {
          flex: '1 0 0',
          textAlign: 'left',
          margin: 0,
        },
        margin: 0,
        padding: 0,
      },
    },
    section: {
      true: {
        padding: '2rem 1rem 0 1rem',
      },
    },
    center: {
      /* Center the content contained within */
      true: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    stripPadding: {
      /* Remove padding from the container */
      true: {
        padding: '0',
      },
    },
    expand: {
      /* Expand vertically and horizontally to fill the rest of the page */
      true: {
        minHeight: '100vh',
        height: '100vh',
        minWidth: '100vw',
        width: '100vw',
      },
    },
    expandX: {
      true: {
        minWidth: '80vw',
        width: '80vw',
      },
    },
    dotted: {
      true: {
        $$dotSpace: '1.5rem',
        $$dotSize: '0.0625rem',
        background: `linear-gradient(90deg, $appBackground calc($$dotSpace - $$dotSize), transparent 1%) center,
                     linear-gradient($appBackground calc($$dotSpace - $$dotSize), transparent 1%) center, 
                     $solidBackground`,
        backgroundSize: '$$dotSpace $$dotSpace',
      },
    },
  },
})

export const defaultButtonStyle = {
  backgroundColor: '$elementBackground',
  color: '$highContrast',
  height: '2.75rem',
  border: 'none',
  borderRadius: '0.5rem',
  fontSize: 'medium',
  userSelect: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 1rem 0 1rem',
  width: 'auto',
}

export const AnchorButton = styled('a', {
  ...defaultButtonStyle,
  '&:hover': {
    backgroundColor: '$elementHover',
  },
  '&:active': {
    backgroundColor: '$elementActive',
  },
  "&[data-state='open']": {
    backgroundColor: '$elementActive',
  },
  variants: {
    thin: {
      true: {
        height: '2.75rem',
        padding: '0 0.5rem 0 0.5rem',
      },
    },
    block: {
      true: {
        width: '100%',
      },
    },
    icon: {
      true: {
        width: '2.75rem',
        background: 'none',
        padding: '0',
      },
    },
  },
})
export const Button = styled('button', {
  ...defaultButtonStyle,

  '&:hover': {
    backgroundColor: '$elementHover',
  },
  '&:active': {
    backgroundColor: '$elementActive',
  },
  "&[data-state='open']": {
    backgroundColor: '$elementActive',
  },
  '&:disabled': {
    opacity: '30%',
    cursor: 'not-allowed',
  },

  variants: {
    search: {
      desktop: {
        width: '80%',
        marginBottom: '1rem',
        gap: '2rem',
      },
    },
    thin: {
      true: {
        height: '2.75rem',
        padding: '0 0.5rem 0 0.5rem',
      },
    },
    warning: {
      true: {
        backgroundColor: '$amber4',
        '&:hover': {
          backgroundColor: '$amber6',
        },
      },
    },
    block: {
      true: {
        width: '100%',
      },
    },
    icon: {
      true: {
        width: '2.75rem',
        background: 'none',
        padding: '0',
      },
    },
    active: {
      true: {
        backgroundColor: '$neutral6',
        '&:hover': { backgroundColor: '$elementHover' },
      },
    },
    /* Press and hold animation */
    animate: {
      true: {
        transition:
          'transform 250ms cubic-bezier(.2, .8, .4, 1), background-color 250ms ease-in-out',
        '&:hover': {
          transform: 'scale(1.05)',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
      },
    },
  },
})

export const Footnote = styled('p', {
  fontSize: '$footnote',
  variants: {
    muted: {
      true: {
        color: '$lowContrast',
      },
    },
    center: {
      true: {
        textAlign: 'center',
      },
    },
  },
})

export const StandardDiv = styled('div', {
  all: 'unset',
  justifyContent: 'space-between',
  display: 'flex',
  flexGrow: 2,
  padding: '0.75rem',
  background: '$neutral3',
  borderRadius: '0.5rem',
})

export const Wrapper = styled('div', {
  width: '100%',
  variants: {
    inline: {
      true: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
      },
    },
  },
})

export const FlexContainer = styled('div', {
  display: 'flex',
  gap: '1rem',
  variants: {
    column: {
      true: {
        flexDirection: 'column',
      },
    },
    centred: {
      true: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
  },
})

export const Banner = styled('div', {
  width: '100%',
  backgroundColor: '$subtleBackground',
  borderRadius: '0.5rem',
  minHeight: '10rem',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  padding: '1rem',
  variants: {
    mobileTimeline: {
      true: {
        padding: '4rem 2rem',
        border: '2px solid $separator',
        gap: '2rem',
      },
    },
    level: {
      warning: {
        backgroundColor: '$warningBackground',
      },
      danger: {
        backgroundColor: '$dangerBackground',
      },
      alert: {
        backgroundColor: '$alertBackground',
      },
    },
    thin: {
      true: {
        minHeight: 'auto',
      },
    },
    inline: {
      true: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
      },
    },
  },
})

/* The following code was inspired by the Radix UI scroll area codesandbox:
 * https://codesandbox.io/s/vwrqpu?module=App.js&file=/App.js
 */
export const Area = styled(ScrollArea, {
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
})

export const BaseViewPort = styled(ScrollAreaViewport, {
  height: '100%',
  width: '100%',
  backgroundColor: '$appBackground',
})

export const Viewport = styled(BaseViewPort, {
  // https://github.com/radix-ui/primitives/issues/1666#issuecomment-1431497744
  // which lead me to https://github.com/radix-ui/primitives/issues/926
  '> div[style]': {
    display: 'block !important',
  },
})

export const Scrollbar = styled(ScrollAreaScrollbar, {
  display: 'flex',
  userSelect: 'none',
  touchAction: 'none',
  padding: '0.125rem',

  '&[data-orientation="vertical"]': { width: '0.75rem' },
  '&[data-orientation="horizontal"]': {
    flexDirection: 'column',
    height: '0.75rem',
  },

  backgroundColor: '$subtleBackground',
  zIndex: 6,
})

export const Thumb = styled(ScrollAreaThumb, {
  flex: 1,
  backgroundColor: '$solidBackground',
  borderRadius: '0.375rem',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    minWidth: '2.75rem',
    minHeight: '2.75rem',
  },
  '&:hover, &:active': {
    backgroundColor: '$solidHover',
  },
})

export const Corner = styled(ScrollAreaCorner, {
  background: '$subtleBackground',
})

export const Checkbox = styled(CheckboxPrimitive, {
  all: 'unset',
  cursor: 'pointer',
  boxSizing: 'border-box',
  backgroundColor: '$elementBackground',
  width: '1.75rem',
  minWidth: '1.75rem',
  height: '1.75rem',
  minHeight: '1.75rem',
  borderRadius: '0.375rem',
  '&:hover': { backgroundColor: '$elementHover' },
  '&:focus': {
    backgroundColor: '$appBackground',
    border: '0.0625rem solid $elementBorder',
  },
})

export const Indicator = styled(CheckboxIndicator, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

export const Hr = styled('hr', {
  backgroundColor: '$neutral7',
  border: 'none',
  height: DIVIDER_HEIGHT,
  width: 'auto',
  variants: {
    inset: {
      true: {
        width: '80%',
        marginLeft: '10%',
      },
    },
  },
})

export const SubText = styled('p', {
  color: '$sand10',
  fontSize: '$sm',
})
